import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { GetCampaignDescriptionVm, UserDto } from '../models';
import { StateService } from '../../utils';
import { EnvironmentVariables } from '../../environment-variables';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LookupService extends StateService<State> {
  private campaignsApi = `${this.environment.apiPath}/campaigns`;
  private tenantsApi = `${this.environment.apiPath}/tenants`;
  private usersApi = `${this.environment.apiPath}/users`;

  defaultAvatar: string = '/assets/images/avatar.png';

  constructor(
    private http: HttpClient,
    private environment: EnvironmentVariables,
    private sanitizer: DomSanitizer
  ) {
    super(initalState);
  }

  private loadLargeAvatar(userId: number) {
    this.http
      .get(`${this.usersApi}/${userId}/avatar/large`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(map((res) => res.body))
      .subscribe((blob) => {
        const largeAvatar = this.sanitizer.bypassSecurityTrustUrl(
          URL.createObjectURL(blob!)
        );
        this.state = {
          largeAvatars: {
            ...this.state.largeAvatars,
            [userId]: largeAvatar,
          },
        };
      });
  }

  private loadSmallAvatar(userId: number) {
    this.http
      .get(`${this.usersApi}/${userId}/avatar`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(map((res) => res.body))
      .subscribe((blob) => {
        const smallAvatar = this.sanitizer.bypassSecurityTrustUrl(
          URL.createObjectURL(blob!)
        );
        this.state = {
          smallAvatars: {
            ...this.state.smallAvatars,
            [userId]: smallAvatar,
          },
        };
      });
  }

  campaignDescription$(campaignId: number) {
    return this.select(
      (state, campaignId) => state.campaigns[campaignId],
      () =>
        this.http
          .get<GetCampaignDescriptionVm>(
            `${this.campaignsApi}/${campaignId}/description`
          )
          .subscribe(
            (vm) =>
              (this.state = {
                campaigns: {
                  ...this.state.campaigns,
                  [campaignId]: vm.description,
                },
              })
          ),
      campaignId
    );
  }

  largeAvatar$(userId: number) {
    return this.select(
      (state, userId) => state.largeAvatars[userId],
      () => this.loadLargeAvatar(userId),
      userId
    ).pipe(
      map(
        (avatar) =>
          avatar || this.sanitizer.bypassSecurityTrustUrl(this.defaultAvatar)
      )
    );
  }

  refreshAvatars(userId: number) {
    this.loadLargeAvatar(userId);
    this.loadSmallAvatar(userId);
  }

  smallAvatar$(userId: number) {
    return this.select(
      (state, userId) => state.smallAvatars[userId],
      () => this.loadSmallAvatar(userId),
      userId
    ).pipe(
      map(
        (avatar) =>
          avatar || this.sanitizer.bypassSecurityTrustUrl(this.defaultAvatar)
      )
    );
  }

  tenantDescription$(tenantId: number) {
    return this.select(
      (state, tenantId) => state.tenants[tenantId],
      () =>
        this.http
          .get<string>(`${this.tenantsApi}/${tenantId}/description`)
          .subscribe(
            (description) =>
              (this.state = {
                tenants: { ...this.state.tenants, [tenantId]: description },
              })
          ),
      tenantId
    );
  }

  tenantTimezone$(tenantId: number) {
    return this.select(
      (state, tenantId) => state.timezones[tenantId],
      () =>
        this.http
          .get<string>(`${this.tenantsApi}/${tenantId}/timezone`)
          .subscribe(
            (timezone) =>
              (this.state = {
                timezones: { ...this.state.timezones, [tenantId]: timezone },
              })
          ),
      tenantId
    );
  }

  userName$(userId: number) {
    return this.select(
      (state, userId) => state.users[userId],
      () =>
        this.http
          .get<UserDto>(`${this.usersApi}/${userId}/name`)
          .subscribe(
            (user) =>
              (this.state = { users: { ...this.state.users, [userId]: user } })
          ),
      userId
    );
  }
}

interface State {
  campaigns: { [campaignId: number]: string };
  largeAvatars: { [userId: number]: SafeUrl };
  smallAvatars: { [userId: number]: SafeUrl };
  tenants: { [tenantId: number]: string };
  timezones: { [tenantId: number]: string };
  users: { [userId: number]: UserDto };
}

const initalState: State = {
  campaigns: {},
  tenants: {},
  timezones: {},
  users: {},
  largeAvatars: {},
  smallAvatars: {},
};
